import { useCallback, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
// AIDEV-NOTE: `scheduler` je přímá závislost jen kvůli této sondě. Musí sahat na TUTÉŽ instanci jako react-dom,
// jinak by sonda měřila jiný Scheduler, než na kterém běží React. Při upgradu Reactu proto srovnej verzi
// `scheduler` v package.json s `dependencies` react-dom; hlídá to test schedulerVersion.test.ts. [TD-467]
import { unstable_NormalPriority, unstable_scheduleCallback } from "scheduler";

import { hardNavigate } from "utils/navigation/hardNavigate";

import { NAV_STALL_MS, NAV_STALL_PROBE_MS } from "./navStallConfig";

/**
 * Hlídač navigace z výsledků tréninku. [TD-467]
 *
 * AIDEV-NOTE: Safari občas přestane vykreslovat ne-sync práci Reactu (Scheduler pošle zprávu přes
 * MessageChannel, WebKit po zániku síťového procesu tiše zavře porty a Scheduler zůstane s
 * `isMessageLoopRunning === true`). `navigate("/")` pak změní adresu (pushState), ale trasa se nikdy
 * necommitne a student vidí výsledky až do F5. Sync práce (kliky, useSyncExternalStore) dál funguje,
 * proto je tohle jediné místo, které se dá spolehlivě hlídat.
 * Hook po `arm()` počká NAV_STALL_MS; když je komponenta pořád připojená, navigace se necommitla.
 * Pak změří, jestli žije Scheduler (callback přes scheduler), pošle událost do Sentry
 * a udělá tvrdý přechod. Reload je bezpečný: session je v kroku výsledků už zavřená.
 *
 * Odpojení se sleduje přes useLayoutEffect, ne useEffect: layout cleanup běží přímo v commitu,
 * kdežto pasivní efekty ne-sync commitu by šly zase přes Scheduler, tedy přesně tu cestu, která vázne.
 */
export const useNavigationStallWatchdog = (): { arm: (targetPath: string) => void } => {
  const location = useLocation();

  const mountedRef = useRef(true);
  const routerPathRef = useRef(location.pathname);
  const schedulerAliveRef = useRef(false);
  const armedAtRef = useRef<number | undefined>(undefined);
  const timersRef = useRef<Set<number>>(new Set());

  useLayoutEffect(() => {
    routerPathRef.current = location.pathname;
  }, [location.pathname]);

  useLayoutEffect(() => {
    mountedRef.current = true;
    const timers = timersRef.current;
    return () => {
      mountedRef.current = false;
      timers.forEach(id => {
        window.clearTimeout(id);
      });
      timers.clear();
    };
  }, []);

  const schedule = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timersRef.current.delete(id);
      fn();
    }, ms);
    timersRef.current.add(id);
  }, []);

  const onStall = useCallback(
    (targetPath: string) => {
      if (!mountedRef.current) return;

      // Skrytou kartu nereloadujeme: prohlížeč jí throttluje timery i render a student ji nevidí.
      if (document.visibilityState === "hidden") {
        schedule(() => {
          onStall(targetPath);
        }, NAV_STALL_MS);
        return;
      }

      // AIDEV-NOTE: sondujeme Scheduler přímo, ne přes setState. DefaultLane update se totiž vykreslí
      // i s jakýmkoli nejbližším sync renderem (klik studenta, odpověď getCurrentUser), takže sonda přes
      // React by u klepajícího studenta hlásila "živý" i při mrtvém Scheduleru. [TD-467]
      schedulerAliveRef.current = false;
      schedule(() => {
        unstable_scheduleCallback(unstable_NormalPriority, () => {
          schedulerAliveRef.current = true;
        });
      }, 0);

      schedule(() => {
        if (!mountedRef.current) return;

        Sentry.captureMessage("TD-467: navigace z výsledků se necommitla", {
          level: "warning",
          tags: { td467: "nav-stall", schedulerAlive: String(schedulerAliveRef.current) },
          extra: {
            clockDriftMs: Date.now() - (performance.timeOrigin + performance.now()),
            visibilityState: document.visibilityState,
            windowPathname: window.location.pathname,
            routerPathname: routerPathRef.current,
            msSinceArm: armedAtRef.current === undefined ? null : Math.round(performance.now() - armedAtRef.current),
          },
        });

        const go = (): void => {
          if (mountedRef.current) hardNavigate(targetPath);
        };
        Sentry.flush(2000).then(go, go);
      }, NAV_STALL_PROBE_MS);
    },
    [schedule],
  );

  const arm = useCallback(
    (targetPath: string) => {
      Sentry.addBreadcrumb({
        category: "td467",
        message: "navigate",
        data: { to: targetPath, pathname: window.location.pathname },
      });

      // Rage click volá arm() opakovaně. Časovač se nesmí posouvat, jinak by student, který klepe
      // každých pár set ms, hlídač nikdy nespustil.
      if (armedAtRef.current !== undefined) return;
      armedAtRef.current = performance.now();

      schedule(() => {
        onStall(targetPath);
      }, NAV_STALL_MS);
    },
    [onStall, schedule],
  );

  return { arm };
};
